import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PayrollRulesStep = ({ data, organizationInfo, onComplete, onBack, onCancel }) => {
  const [formData, setFormData] = useState({
    payPeriod: data?.payPeriod || '',
    overtimeRate: data?.overtimeRate || '1.5',
    sundayRate: data?.sundayRate || '2.0',
    publicHolidayRate: data?.publicHolidayRate || '2.5',
    nasfundEmployeeRate: data?.nasfundEmployeeRate || '6.0',
    nasfundEmployerRate: data?.nasfundEmployerRate || '8.4',
    minimumWage: data?.minimumWage || '3.50',
    leaveAnnualDays: data?.leaveAnnualDays || '21',
    leaveSickDays: data?.leaveSickDays || '10'
  });
  const [errors, setErrors] = useState({});
  const [useTemplate, setUseTemplate] = useState(false);
  const [saving, setSaving] = useState(false);

  const payPeriodOptions = [
    { value: 'fortnightly', label: 'Fortnightly (Every 2 weeks)' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'weekly', label: 'Weekly' }
  ];

  const templates = [
    {
      id: 'standard',
      name: 'Standard PNG Employment Act',
      description: 'Default rates as per PNG Employment Act 1978',
      values: {
        overtimeRate: '1.5',
        sundayRate: '2.0',
        publicHolidayRate: '2.5',
        nasfundEmployeeRate: '6.0',
        nasfundEmployerRate: '8.4',
        minimumWage: '3.50',
        leaveAnnualDays: '21',
        leaveSickDays: '10'
      }
    },
    {
      id: 'mining',
      name: 'Mining & Resources Sector',
      description: 'Enhanced rates for mining industry',
      values: {
        overtimeRate: '2.0',
        sundayRate: '2.5',
        publicHolidayRate: '3.0',
        nasfundEmployeeRate: '6.0',
        nasfundEmployerRate: '8.4',
        minimumWage: '5.00',
        leaveAnnualDays: '28',
        leaveSickDays: '15'
      }
    },
    {
      id: 'government',
      name: 'Government Sector',
      description: 'Public sector employment rates',
      values: {
        overtimeRate: '1.5',
        sundayRate: '2.0',
        publicHolidayRate: '2.5',
        nasfundEmployeeRate: '6.0',
        nasfundEmployerRate: '8.4',
        minimumWage: '4.00',
        leaveAnnualDays: '25',
        leaveSickDays: '12'
      }
    }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const applyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      ...template?.values
    }));
    setUseTemplate(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.payPeriod) {
      newErrors.payPeriod = 'Pay period is required';
    }

    if (!formData?.overtimeRate || parseFloat(formData?.overtimeRate) < 1) {
      newErrors.overtimeRate = 'Overtime rate must be at least 1.0x';
    }

    if (!formData?.sundayRate || parseFloat(formData?.sundayRate) < 1) {
      newErrors.sundayRate = 'Sunday rate must be at least 1.0x';
    }

    if (!formData?.nasfundEmployeeRate || parseFloat(formData?.nasfundEmployeeRate) < 0) {
      newErrors.nasfundEmployeeRate = 'NASFUND employee rate is required';
    }

    if (!formData?.nasfundEmployerRate || parseFloat(formData?.nasfundEmployerRate) < 0) {
      newErrors.nasfundEmployerRate = 'NASFUND employer rate is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      try {
        setSaving(true);
        
        // Save payroll rules (can be saved to payroll_settings table)
        // For now, just pass data forward
        
        onComplete?.({
          ...formData,
          complianceConfigured: true // Mark compliance as configured
        });
      } catch (err) {
        console.error('Error saving payroll rules:', err);
        setErrors({ submit: err?.message || 'Failed to save payroll rules' });
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <Icon name="Calculator" size={24} className="text-emerald-600 mr-2" />
          <h2 className="text-2xl font-bold text-gray-900">Payroll Rules Configuration</h2>
        </div>
        <p className="text-gray-600">
          Configure payroll rules using intelligent templates based on PNG Employment Act requirements.
        </p>
      </div>
      {/* Template Selection */}
      <div className="mb-6">
        <Button
          type="button"
          variant="outline"
          onClick={() => setUseTemplate(!useTemplate)}
          iconName="Sparkles"
          className="mb-4"
        >
          {useTemplate ? 'Hide Templates' : 'Use Quick Template'}
        </Button>

        {useTemplate && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {templates?.map(template => (
              <div
                key={template?.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer"
                onClick={() => applyTemplate(template)}
              >
                <h4 className="font-semibold text-gray-900 mb-1">{template?.name}</h4>
                <p className="text-sm text-gray-600">{template?.description}</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-3"
                  iconName="Check"
                >
                  Apply Template
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Error Display */}
        {errors?.submit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <Icon name="AlertCircle" size={20} className="text-red-600 mr-3 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">Error</p>
              <p className="text-sm text-red-700 mt-1">{errors?.submit}</p>
            </div>
          </div>
        )}

        {/* Pay Period */}
        <Select
          label="Pay Period"
          required
          options={payPeriodOptions}
          value={formData?.payPeriod}
          onChange={(value) => handleChange('payPeriod', value)}
          error={errors?.payPeriod}
          placeholder="Select pay period frequency"
        />

        {/* Overtime & Special Rates */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Overtime & Special Rates</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Overtime Rate (Multiplier)"
              type="number"
              step="0.1"
              required
              value={formData?.overtimeRate}
              onChange={(e) => handleChange('overtimeRate', e?.target?.value)}
              error={errors?.overtimeRate}
              placeholder="1.5"
              description="PNG standard: 1.5x"
            />
            <Input
              label="Sunday Rate (Multiplier)"
              type="number"
              step="0.1"
              required
              value={formData?.sundayRate}
              onChange={(e) => handleChange('sundayRate', e?.target?.value)}
              error={errors?.sundayRate}
              placeholder="2.0"
              description="PNG standard: 2.0x"
            />
            <Input
              label="Public Holiday Rate (Multiplier)"
              type="number"
              step="0.1"
              required
              value={formData?.publicHolidayRate}
              onChange={(e) => handleChange('publicHolidayRate', e?.target?.value)}
              placeholder="2.5"
              description="PNG standard: 2.5x"
            />
          </div>
        </div>

        {/* NASFUND Rates */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">NASFUND Contribution Rates</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Employee Contribution (%)"
              type="number"
              step="0.1"
              required
              value={formData?.nasfundEmployeeRate}
              onChange={(e) => handleChange('nasfundEmployeeRate', e?.target?.value)}
              error={errors?.nasfundEmployeeRate}
              placeholder="6.0"
              description="Standard: 6%"
            />
            <Input
              label="Employer Contribution (%)"
              type="number"
              step="0.1"
              required
              value={formData?.nasfundEmployerRate}
              onChange={(e) => handleChange('nasfundEmployerRate', e?.target?.value)}
              error={errors?.nasfundEmployerRate}
              placeholder="8.4"
              description="Standard: 8.4%"
            />
          </div>
        </div>

        {/* Minimum Wage & Leave */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Minimum Wage & Leave Policies</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Minimum Hourly Wage (PGK)"
              type="number"
              step="0.01"
              value={formData?.minimumWage}
              onChange={(e) => handleChange('minimumWage', e?.target?.value)}
              placeholder="3.50"
              description="Current: K3.50/hour"
            />
            <Input
              label="Annual Leave Days"
              type="number"
              value={formData?.leaveAnnualDays}
              onChange={(e) => handleChange('leaveAnnualDays', e?.target?.value)}
              placeholder="21"
              description="Standard: 21 days"
            />
            <Input
              label="Sick Leave Days"
              type="number"
              value={formData?.leaveSickDays}
              onChange={(e) => handleChange('leaveSickDays', e?.target?.value)}
              placeholder="10"
              description="Standard: 10 days"
            />
          </div>
        </div>

        {/* Compliance Info */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="Shield" size={20} className="text-emerald-600 mr-3 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">Automatic Compliance Checking</p>
              <p className="text-sm text-emerald-700 mt-1">
                All rates are validated against PNG Employment Act 1978 requirements. The system will alert you if any rates fall below legal minimums.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-6 border-t">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              iconName="ArrowLeft"
              disabled={saving}
            >
              Back
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              iconName="X"
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
          <Button
            type="submit"
            iconName="ArrowRight"
            iconPosition="right"
            loading={saving}
          >
            Continue to System Activation
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PayrollRulesStep;